// Overview `/` — "What needs attention?" (DESIGN §3). Four MetricCards for the chosen period, the Production
// Overview chart (7D/30D/3M) beside four small cards, and the right panel (Today: deadlines + activity; Upcoming).
// No batches yet: one next step (Add your first client / Add Batch) instead of cards of zeros and an empty chart.
// Query: ?period=today|week|month (header dropdown) & range=7d|30d|3m (chart); defaults are left out of the URL.
import './overview.css';
import { useEffect, useState } from 'preact/hooks';
import * as derive from '../../shared/derive.js';
import { OVERVIEW_PERIODS, PERFORMANCE_RANGES } from '../../shared/constants.js';
import { startOfWeek, formatDate } from '../../shared/dates.js';
import { useStore } from '../store.js';
import { setQuery, workspaceHref } from '../router.js';
import { Page } from '../components/page.jsx';
import { Panel } from '../components/panel.jsx';
import { MetricCard } from '../components/metric-card.jsx';
import { CompactDropdown } from '../components/compact-dropdown.jsx';
import { FilterButtons } from '../components/filter-buttons.jsx';
import { AreaChart } from '../components/chart.jsx';
import { TodayPanel } from '../components/today-panel.jsx';
import { Button, EmptyState } from '../components/ui.jsx';
import { openAddBatch, openAddClient } from '../components/forms.jsx';

export const OVERVIEW_RANGES = PERFORMANCE_RANGES.filter((r) => ['7d', '30d', '3m'].includes(r.key));
const DEFAULT_PERIOD = 'today';
const DEFAULT_CHART_RANGE = '30d';
const PREVIOUS_LABEL = { today: 'yesterday', week: 'last week', month: 'last month' };

const pick = (value, allowed, fallback) => (allowed.includes(value) ? value : fallback);
const round1 = (v) => Math.round(v * 10) / 10;
const pp = (v) => `${v > 0 ? '+' : ''}${round1(v)} pp`;
const percent = (v) => (v == null ? null : `${round1(v)}%`);

// Daily points → weekly sums (Monday weeks) so a 3-month chart stays readable.
export function weeklyPoints(points) {
  const weeks = new Map();
  for (const p of points) {
    const key = startOfWeek(p.date);
    const w = weeks.get(key) || { date: key, label: formatDate(key), value: 0 };
    w.value += p.value || 0;
    weeks.set(key, w);
  }
  return [...weeks.values()];
}

function Metrics({ cards, period }) {
  const { activeBatches, due, waitingReview, completed } = cards;
  const prev = PREVIOUS_LABEL[period];
  return (
    <div class="grid-4 ov-metrics">
      <MetricCard
        label="Active batches"
        value={activeBatches.value}
        meta={activeBatches.addedThisWeek ? `+${activeBatches.addedThisWeek} this week` : ''}
        href={workspaceHref({})}
      />
      <MetricCard
        label={due.label}
        value={due.value}
        meta={due.overdue ? `${due.overdue} overdue` : 'None overdue'}
        metaTone={due.overdue ? 'danger' : undefined}
        href={workspaceHref({})}
      />
      <MetricCard label="Waiting review" value={waitingReview.value} href={workspaceHref({ filter: 'review' })} />
      <MetricCard
        label="Completed"
        value={completed.value}
        delta={completed.delta}
        meta={completed.delta != null ? `vs ${prev}` : `${completed.previous} ${prev}`}
        href={workspaceHref({ filter: 'done' })}
      />
    </div>
  );
}

// true while the media query matches (the chart is shorter on phones).
function useMedia(query) {
  const get = () => typeof window !== 'undefined' && !!window.matchMedia?.(query).matches;
  const [match, setMatch] = useState(get);
  useEffect(() => {
    const mq = window.matchMedia?.(query);
    if (!mq) return undefined;
    const on = () => setMatch(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return match;
}

// Production Overview: completed batches per day on 7D, per week on 30D and 3M (a few completions a month read as a
// trend, not as 0→1→0 spikes), drawn as a smooth curve over a gradient fill.
function ProductionPanel({ production, range, onRange }) {
  const weekly = range !== '7d';
  const points = weekly ? weeklyPoints(production.points) : production.points;
  const data = points.some((p) => p.value > 0) ? points : []; // nothing completed → the chart's empty state
  const phone = useMedia('(max-width: 767.98px)');
  const long = OVERVIEW_RANGES.find((r) => r.key === range)?.long.toLowerCase() || '';
  return (
    <Panel
      class="ov-production"
      title="Production Overview"
      meta={weekly ? 'Completed per week' : 'Completed per day'}
      actions={
        <FilterButtons
          size="sm"
          variant="text"
          label="Chart range"
          options={OVERVIEW_RANGES.map((r) => ({ value: r.key, label: r.label }))}
          value={range}
          onChange={onRange}
        />
      }
    >
      <svg class="ov-defs" width="0" height="0" aria-hidden="true" focusable="false">
        <defs>
          <linearGradient id="ov-area-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" class="ov-stop-top" />
            <stop offset="1" class="ov-stop-bottom" />
          </linearGradient>
        </defs>
      </svg>
      <div class="ov-chart">
        <AreaChart
          data={data}
          height={phone ? 176 : 236}
          tone="success"
          smooth
          label={`Completed batches ${weekly ? 'per week' : 'per day'}, ${long}`}
          emptyText="No completed batches yet."
        />
      </div>
    </Panel>
  );
}

// The four small cards beside the chart. ON TIME / OUTPUT follow the chart range (delta vs the previous range);
// ACTIVE EDITING = In Progress in Video Editing; BLOCKED replaces the brief's second WAITING REVIEW (see report).
function SideCards({ side, blocked, range }) {
  const r = OVERVIEW_RANGES.find((x) => x.key === range);
  const perf = `#/performance?range=${range}`;
  return (
    <div class="ov-side">
      <MetricCard size="sm" label={`On time · ${r.label}`} value={percent(side.onTime.value)} delta={side.onTime.delta} deltaFormat={pp} href={perf} />
      <MetricCard size="sm" label={`Output · ${r.label}`} value={side.output.value} delta={side.output.delta} href={perf} />
      <MetricCard size="sm" label="Active editing" value={side.activeEditing} href={workspaceHref({})} />
      <MetricCard size="sm" label="Blocked" value={blocked} href={workspaceHref({})} />
    </div>
  );
}

// A workspace with nothing to measure yet: one obvious next step instead of cards of zeros and an empty chart.
function FirstStep({ hasClients }) {
  return hasClients ? (
    <EmptyState
      class="ov-first"
      title="No batches yet."
      description="Add the first batch for one of your clients — this page fills in as work moves."
      action={<Button variant="primary" icon="plus" onClick={() => openAddBatch({})}>Add Batch</Button>}
    />
  ) : (
    <EmptyState
      class="ov-first"
      title="No clients yet."
      description="Add your first client to start production. Batches, deadlines and progress show up here."
      action={<Button variant="primary" icon="plus" onClick={() => openAddClient()}>Add your first client</Button>}
    />
  );
}

export function OverviewView({ route }) {
  const { state, index, today } = useStore();
  const query = route?.query || {};
  const period = pick(query.period, OVERVIEW_PERIODS.map((p) => p.key), DEFAULT_PERIOD);
  const range = pick(query.range, OVERVIEW_RANGES.map((r) => r.key), DEFAULT_CHART_RANGE);
  const data = derive.overviewData(state, index, today, period, { range });
  const blocked = state.batches.filter((b) => b.status === 'Blocked' && index.clientsById.get(b.clientId)?.status === 'Active').length;

  const periodPicker = (
    <CompactDropdown
      ariaLabel={`Period: ${OVERVIEW_PERIODS.find((p) => p.key === period).label}`}
      value={period}
      options={OVERVIEW_PERIODS.map((p) => ({ value: p.key, label: p.label }))}
      onChange={(v) => setQuery({ period: v === DEFAULT_PERIOD ? '' : v })}
      placement="bottom-end"
      width={180}
    />
  );

  const aside = <TodayPanel state={state} index={index} today={today} />;
  if (!state.batches.length) {
    return (
      <Page title="Overview" aside={aside} asideLabel="Today and upcoming">
        <FirstStep hasClients={state.clients.length > 0} />
      </Page>
    );
  }

  return (
    <Page title="Overview" actions={periodPicker} aside={aside} asideLabel="Today and upcoming">
      <Metrics cards={data.cards} period={data.period.key} />
      <div class="ov-block">
        <ProductionPanel production={data.production} range={range} onRange={(v) => setQuery({ range: v === DEFAULT_CHART_RANGE ? '' : v })} />
        <SideCards side={data.side} blocked={blocked} range={range} />
      </div>
    </Page>
  );
}
